import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../components/button'
import { Input } from '../components/input'
import { Layout } from '../components/layout'
import { getUser, getUserTeams } from '../firebase'
import { createRundown } from '../firestore'
import TimezoneSelect from '../components/interactives/TimezoneSelect.jsx'
import Select from '../components/Select.jsx'
import { addDays } from '@rundown-studio/timeutils'
import { useTemplatesByTeam, useCloneRundown } from '../entities/rundown/index.ts'

const browserTz = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone

export default function Create() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState(null)
  const [teams, setTeams] = useState([])

  const [config, setConfig] = useState({
    name: '',
    timezone: browserTz,
    teamId: localStorage.getItem('teamId') || '',
    eventId: null,
    templateId: '',
  })

  const { data: templates = [] } = useTemplatesByTeam({
    params: { teamId: config.teamId },
    options: { enabled: !!config.teamId },
  })

  const { mutateAsync: cloneRundown } = useCloneRundown({})

  const patchConfig = useCallback((key, value) => {
    setConfig((prev) => ({
      ...prev,
      [key]: value,
    }))
  }, [])

  async function handleCreateRundown(event) {
    event.preventDefault()

    setLoading(true)

    if (config.templateId) {
      const newRundown = await cloneRundown({
        rundownId: config.templateId,
        teamId: config.teamId,
        name: config.name || 'New rundown',
      })
      return navigate(`/rundown/${newRundown?.id}`)
    }

    const startTime = addDays(new Date(), 1, { timezone: browserTz })
    startTime.setHours(9, 0, 0, 0)

    const { data } = await createRundown({
      name: config.name || 'New rundown',
      teamId: config.teamId,
      timezone: config.timezone,
      startTime,
      eventId: config.eventId,
    })

    return navigate(`/rundown/${data?.id}`)
  }

  async function getUserAndTeams() {
    setUser(await getUser())
    setTeams(await getUserTeams())
  }

  useEffect(() => {
    getUserAndTeams()
  }, [])

  useEffect(() => {
    patchConfig('eventId', searchParams.get('event'))
  }, [patchConfig, searchParams])

  return (
    <Layout title="Create a new rundown">
      <form
        className="space-y-4"
        onSubmit={(event) => handleCreateRundown(event)}
      >
        {/* Name */}
        <Input
          label="Name"
          placeholder="My new rundown..."
          autofocus
          required
          value={config.name}
          onChange={(name) => patchConfig('name', name)}
        />
        {/* Hidden input value for DEV purposes */}
        <input
          value={config.eventId || undefined}
          className="bg-black h-0 hidden"
          readOnly
        />

        {/* Timezone */}
        <div className="">
          <span className="block pt-2 text-blue-500 text-xs">Timezone</span>
          <TimezoneSelect
            className="mt-1 hover:brightness-100"
            value={config.timezone}
            setValue={(timezone) => patchConfig('timezone', timezone)}
          />
        </div>

        {/* Team */}
        <Select
          label="Team"
          value={config.teamId}
          required={teams?.length > 0}
          disabled={!teams?.length}
          options={[
            {
              name: 'Choose a team...',
              value: '',
            },
            ...teams.map((team) => ({
              name: team.name,
              value: team.id,
            })),
          ]}
          onChange={(teamId) => {
            patchConfig('teamId', teamId)
          }}
        />

        {/* Template */}
        <Select
          label="Start from template"
          tooltip="Convert an existing rundown into a template and use it as a starting point."
          value={config.templateId}
          options={[
            {
              name: 'Blank rundown',
              value: '',
            },
            ...templates.map((t) => ({
              name: t.name,
              value: t.id,
            })),
          ]}
          onChange={(templateId) => {
            patchConfig('templateId', templateId)
          }}
        />

        <div className="flex gap-6">
          {user?.uid && (
            <Button
              text="Back"
              colour="dark"
              onClick={() => {
                if (config.eventId) {
                  navigate(
                    `/dashboard/${config.teamId}/event/${config.eventId}`,
                  )
                } else {
                  navigate('/dashboard')
                }
              }}
              className="w-full"
              style={{ flexGrow: 1 }}
              type="button"
              loading={loading}
            />
          )}
          <Button
            text={loading ? 'Creating...' : 'Create rundown'}
            className="w-full"
            style={{ flexGrow: 1 }}
            type="submit"
            loading={loading}
          />
        </div>
      </form>
    </Layout>
  )
}
